/**
 * BatchService
 *
 * REST client for batch execution (TONIC-PEX-IMPL-FE-001 Task 6, §6a).
 *
 * Three writes — run a batch, cancel a batch, cancel a run — and one read,
 * `listBatches`, which is a grouping's run history. `GET /batches/{id}`,
 * `/report` and the per-member `/rerun` were deleted: live member state is
 * rebuilt from per-run reads (see `useBatchRun`), and re-running one script is
 * a normal single run.
 *
 * No retry and no pacing here — the Middleware paces the fan-out.
 *
 * @module shared/middleware/services/BatchService
 */

import { noCacheAxios } from '../../../shared/config/cachedAxiosConfig';
import { BATCH_ENDPOINTS } from '../config/executionApiEndpoints';
import { buildExecutionHeaders, startTransaction } from './ExecutionHeadersService';
import { ProjectHeadersService } from './ProjectHeadersService';
import logger from '../../services/logging/logger';

/** Same runner repo the single-run path submits. */
const DEFAULT_RUNNER_REPO = 'https://github.com/cmdquality/platfrom-test-execution.git';

/** Cancel is idempotent — the same key must ride every retry of one intent. */
const idempotencyKey = (scope, id) => `${scope}-${id}-${Date.now().toString(36)}`;

export const BatchService = {
  /**
   * Submit a grouping for parallel execution.
   *
   * One GTID for the whole batch — the Middleware mints the per-member children
   * and paces the fan-out, so there is deliberately no retry or pacing here.
   *
   * The response carries every member's `runId` and `logPath` before anything
   * runs, which is what makes the `stream.json` backfill map known at submit
   * time. It is returned untouched.
   */
  create: async (projectId, { runnableGroupPath, projectName, gtid }, userData, options = {}) => {
    if (!runnableGroupPath) throw new Error('A batch needs the grouping file path.');

    const tenantId = options.tenantId || ProjectHeadersService.getTenantId(userData);
    const s3Bucket = options.s3Bucket || ProjectHeadersService.getBucketName(userData);
    const headers = await buildExecutionHeaders(userData, { ...options, gtid, tenantId, bucketName: s3Bucket });

    // Today's single-run body with runnableFilePath swapped for runnableGroupPath
    const body = {
      projectId,
      tenantId,
      divisionId: userData?.divisionId ?? null,
      projectName,
      s3Bucket,
      repo: options.repo || DEFAULT_RUNNER_REPO,
      runnableGroupPath,
    };

    // tenantId rides the query string — the controller reads it with
    // @RequestParam, so a body-only value is a 400 before the handler runs.
    const response = await noCacheAxios.post(
      BATCH_ENDPOINTS.create(projectId, tenantId), body, { headers }
    );
    logger.info('Batch submitted', {
      projectId,
      runnableGroupPath,
      batchId: response.data?.batchId,
      members: response.data?.members?.length ?? 0,
    });
    // 202 — the record is written, the fan-out continues in the background. A
    // member can still turn SUBMIT_FAILED after this returns; that is a row
    // state, never a page error (BR-33).
    return response.data;
  },

  /**
   * Cancel the whole batch.
   *
   * Returns 202 with `{ affected, alreadyTerminal, settleWindowSeconds }` — the
   * caller reports "cancelling N — M already finished" rather than "all
   * stopped", and holds non-terminal rows at CANCELLING for the settle window.
   */
  cancelBatch: async (batchId, userData, options = {}) => {
    const headers = await buildExecutionHeaders(userData, {
      ...options,
      extra: { 'Idempotency-Key': options.idempotencyKey || idempotencyKey('batch', batchId) },
    });
    const response = await noCacheAxios.post(BATCH_ENDPOINTS.cancel(batchId), {}, { headers });
    logger.info('Batch cancel requested', { batchId, ...response.data });
    return response.data;
  },

  /**
   * Cancel one script — a batch member or a standalone run. Same 202 shape.
   */
  cancelRun: async (runId, userData, options = {}) => {
    const headers = await buildExecutionHeaders(userData, {
      ...options,
      extra: { 'Idempotency-Key': options.idempotencyKey || idempotencyKey('run', runId) },
    });
    const response = await noCacheAxios.post(BATCH_ENDPOINTS.cancelRun(runId), {}, { headers });
    logger.info('Run cancel requested', { runId, ...response.data });
    return response.data;
  },

  /**
   * A grouping's past batches, newest first.
   *
   * One call answers the whole history sidebar: each item carries its status,
   * pass rate, script and permutation counts, timings and `reportFolder`, so
   * nothing has to be read from S3 just to draw the list.
   *
   * A batch still running reports `endedAt`, `passRate` and the counts as
   * null. They are returned as they came — null means "not known yet", and
   * coercing it to 0 would read as a real figure.
   *
   * @param {Object} page   `{ limit, cursor }`; omit `cursor` for the first page
   * @returns {Promise<{items: Array, nextCursor: string|null}>}
   */
  listBatches: async (projectId, groupingId, page = {}, userData, options = {}) => {
    if (!projectId || !groupingId) {
      throw new Error('A batch listing needs both projectId and groupingId.');
    }

    const headers = await buildExecutionHeaders(userData, options);
    const response = await noCacheAxios.get(
      BATCH_ENDPOINTS.listBatches(projectId, groupingId, page), { headers }
    );

    const { items = [], nextCursor = null } = response.data || {};
    logger.debug('Batch history page loaded', { groupingId, count: items.length, more: !!nextCursor });
    return { items, nextCursor };
  },

  /**
   * One GTID for the whole batch — the Middleware mints the per-member
   * children from it. Call once per Run action and thread the result through
   * every request that action triggers.
   */
  newTransaction: () => startTransaction(),
};

export default BatchService;
